import { z } from 'zod';
import {
  ENVELOPE_MAX_BATCH,
  HOLDER_KINDS,
  HolderService,
  RELAY_TTL_DAYS,
  ShareRefusal,
  VaultKey,
  WrongSecretError,
  answerPracticeRequest,
  consentApprovalBytes,
  consentTermsOf,
  parseLinkCode,
  sealHistoryShares,
  createBackup,
  decodeEnvelopeHeader,
  deliveryOf,
  deliveryPayloadSchema,
  dispenseNoticePayloadSchema,
  fhir,
  fromBase64Url,
  jsonBytes,
  openEnvelope,
  parseJsonBytes,
  peerFromDirectory,
  practiceResponseSchema,
  sealDeliveries,
  sealEnvelope,
  createVault,
  deviceRegistration,
  generateDeviceKeys,
  loadDeviceKeys,
  loginResetBytes,
  passwordSecrets,
  replaceRecoveryCode,
  restoreBackup,
  setVaultPassword,
  signBytes,
  toBase64Url,
  unlockWithPassword,
  unlockWithRecoveryCode,
  type Alert,
  type BackupFile,
  type DeviceKeyMaterial,
  type DeviceKeys,
  type DeliveryPayload,
  type DirectoryPharmacy,
  type HeldRecord,
  type MyPatientLink,
  type MyPractice,
  type HolderEvent,
  type PracticePatientLink,
  type PracticeQuery,
  type PracticeRequest,
  type PracticeResponse,
  type PublicAccount,
  type PublicConsent,
  type PublicPractice,
  type Role,
  type VaultKeyring,
} from '@curentia/shared';
import { ApiError, type Api, type AuthResult, type InboxEnvelope } from './api';
import { LiveAlerts } from './alerts';
import { DOORS, type Door } from './doors';
import { DoctorNotices } from './notices';
import { PersonalRecords } from './personal';
import { worksInPractices } from './practices';
import { PracticeError, PracticeRecords, RecordCache, VaultNoticeCursor, VaultRecordStore, VaultSeen, VaultShared } from './records';
import type { LocalStore } from './store';

const DAY_MS = 24 * 60 * 60 * 1000;
/** How long to wait for a record holder's answer before telling the person. */
export const ANSWER_TIMEOUT_MS = 30_000;
/** How often to look for answers while waiting, in case a live alert is missed. */
const ANSWER_POLL_MS = 1500;

/** What the vault holds about the account. */
export interface Profile {
  account: PublicAccount;
  deviceRegistered: boolean;
}

export interface Settings {
  backupReminders: boolean;
  backupEveryDays: number;
  lastBackupAt: string | null;
  recoveryCodeConfirmed: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  backupReminders: true,
  backupEveryDays: 7,
  lastBackupAt: null,
  recoveryCodeConfirmed: false,
};

/** True when reminders are on and the last backup is missing or older than the chosen interval. */
export function isBackupDue(settings: Settings, now: Date): boolean {
  if (!settings.backupReminders) return false;
  if (!settings.lastBackupAt) return true;
  return now.getTime() - Date.parse(settings.lastBackupAt) >= settings.backupEveryDays * DAY_MS;
}

export type Phase = 'loading' | 'fresh' | 'needs_setup' | 'locked' | 'unlocked';

/** Everything the screens show. Nothing here is written to disk. */
export interface Snapshot {
  phase: Phase;
  account: PublicAccount | null;
  deviceId: string | null;
  online: boolean;
  live: boolean;
  settings: Settings;
  backupDue: boolean;
  /** A new recovery code to show once, until the person confirms it. */
  recoveryCode: string | null;
  /** Envelopes waiting for this device. */
  waiting: number;
  /** Goes up whenever the server says one of this account's practices changed, so screens fetch again. */
  practicesVersion: number;
  /** Goes up when a consent this account is part of changes. */
  consentsVersion: number;
  /** Goes up when a patient link this account is part of changes. */
  linksVersion: number;
  /** Goes up when this device takes in records: shares or prescriptions for a practice it holds, or copies for a patient. */
  recordsVersion: number;
  notice: string | null;
}

const EMPTY: Omit<Snapshot, 'phase'> = {
  account: null,
  deviceId: null,
  online: false,
  live: false,
  settings: DEFAULT_SETTINGS,
  backupDue: false,
  recoveryCode: null,
  waiting: 0,
  practicesVersion: 0,
  consentsVersion: 0,
  linksVersion: 0,
  recordsVersion: 0,
  notice: null,
};

export interface SignUpInput {
  door: Door;
  name: string;
  mobile: string;
  password: string;
  email?: string;
  registration?: { council: string; number: string };
  abhaNumber?: string;
  abhaAddress?: string;
}

export interface DeviceOptions {
  /** Name for this device in the account's device list. */
  label: string;
  /** Live alerts need a browser or Node with sockets; tests may turn them off. */
  liveAlerts?: boolean;
  /** How often to look for a record holder's answer while waiting. Tests without live alerts shorten it. */
  answerPollMs?: number;
  now?: () => Date;
}

interface Session {
  vaultKey: VaultKey;
  keyring: VaultKeyring;
  profile: Profile;
  settings: Settings;
  keys: DeviceKeys;
  material: DeviceKeyMaterial;
  /** Kept in memory while unlocked to sign in again when a token expires. */
  loginKey: string | null;
}

interface PendingLogin {
  account: PublicAccount;
  password: string;
  salt: string;
  loginKey: string;
}

const ITEM = { profile: 'profile', device: 'device', settings: 'settings' } as const;

/** The practice a relayed envelope is addressed to as a record holder, read from its header. */
function addressedPractice(envelope: InboxEnvelope): string | undefined {
  try {
    return decodeEnvelopeHeader(fromBase64Url(envelope.header)).to.practice;
  } catch {
    return undefined;
  }
}

/** Null when the server answers 404, for lookups where absence is an answer. */
async function notFoundAsNull<T>(request: Promise<T>): Promise<T | null> {
  try {
    return await request;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

interface Waiting {
  resolve(response: PracticeResponse): void;
  reject(err: Error): void;
  timer: ReturnType<typeof setTimeout>;
}

const backupContentsSchema = z.object({
  v: z.literal(1),
  items: z.record(z.string(), z.unknown()),
});

/** A step the person can fix, with a message fit to show. */
export class DeviceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeviceError';
  }
}

/**
 * The app's core, without React. It owns the vault key and device keys while
 * unlocked, keeps decrypted data in memory only, and talks to the server.
 * Screens read its snapshot and call its methods.
 */
export class CurentiaDevice {
  private state: Snapshot = { phase: 'loading', ...EMPTY };
  private readonly listeners = new Set<() => void>();
  private store: LocalStore | null = null;
  private session: Session | null = null;
  private pending: PendingLogin | null = null;
  private readonly alerts: LiveAlerts;
  // Per unlocked session, in memory only; lock() forgets them.
  private practices = new Map<string, MyPractice>();
  private readonly answers = new Map<string, Waiting>();
  private readonly heldStores = new Map<string, VaultRecordStore>();
  private readonly holders = new Map<string, HolderService>();
  private seenStore: VaultSeen | null = null;
  private sharedStore: VaultShared | null = null;
  private personalStore: PersonalRecords | null = null;
  private noticesStore: DoctorNotices | null = null;
  private pinCache: Record<string, string> | null = null;
  private inboxRun: Promise<void> | null = null;
  private inboxAgain = false;
  private answerPoll: ReturnType<typeof setInterval> | null = null;

  constructor(
    readonly api: Api,
    private readonly openStore: () => Promise<LocalStore>,
    private readonly options: DeviceOptions,
  ) {
    this.alerts = new LiveAlerts(api.baseUrl);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Snapshot => this.state;

  private now(): Date {
    return this.options.now?.() ?? new Date();
  }

  private update(patch: Partial<Snapshot>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private async db(): Promise<LocalStore> {
    this.store ??= await this.openStore();
    return this.store;
  }

  private requireSession(): Session {
    if (!this.session) throw new DeviceError('Unlock Curentia first');
    return this.session;
  }

  /** Publishes the unlocked session's state. */
  private publish(patch: Partial<Snapshot> = {}): void {
    const s = this.requireSession();
    this.update({
      phase: 'unlocked',
      account: s.profile.account,
      deviceId: s.keys.id,
      settings: s.settings,
      backupDue: isBackupDue(s.settings, this.now()),
      ...patch,
    });
  }

  async init(): Promise<void> {
    const keyring = await (await this.db()).readKeyring();
    this.update({ phase: keyring ? 'locked' : 'fresh' });
  }

  // ---- Creating and opening the vault -------------------------------------

  async signUp(input: SignUpInput): Promise<void> {
    const door = DOORS[input.door];
    const { salt } = await this.api.prelogin(door.type, input.mobile);
    const { vaultKey, keyring, recoveryCode, loginKey } = await createVault(input.password, salt);
    const { keys, material } = await generateDeviceKeys();
    let result: AuthResult;
    try {
      result = await this.api.register({
        type: door.type,
        name: input.name,
        mobile: input.mobile,
        loginKey,
        ...(input.email ? { email: input.email } : {}),
        ...(input.registration ? { registration: input.registration } : {}),
        ...(input.abhaNumber ? { abhaNumber: input.abhaNumber } : {}),
        ...(input.abhaAddress ? { abhaAddress: input.abhaAddress } : {}),
      });
    } catch (err) {
      vaultKey.destroy();
      throw err;
    }
    await this.adopt({ vaultKey, keyring, material, keys, loginKey, profile: { account: result.account, deviceRegistered: false } });
    this.api.setToken(result.accessToken);
    this.publish({ recoveryCode });
    await this.afterSignIn();
  }

  /** Signs in on a device that has no vault yet. The next step is setUpNewDevice or restoreAfterLogin. */
  async logIn(door: Door, mobile: string, password: string): Promise<void> {
    const type = DOORS[door].type;
    const { salt } = await this.api.prelogin(type, mobile);
    const { loginKey } = await passwordSecrets(password, salt);
    const result = await this.api.login(type, mobile, loginKey);
    this.api.setToken(result.accessToken);
    this.pending = { account: result.account, password, salt, loginKey };
    this.update({ phase: 'needs_setup', account: result.account });
  }

  /** Starts fresh on this device after logIn: a new vault, new keys and a new recovery code. */
  async setUpNewDevice(): Promise<void> {
    const pending = this.pending;
    if (!pending) throw new DeviceError('Sign in first');
    const { vaultKey, keyring, recoveryCode, loginKey } = await createVault(pending.password, pending.salt);
    const { keys, material } = await generateDeviceKeys();
    this.pending = null;
    await this.adopt({ vaultKey, keyring, material, keys, loginKey, profile: { account: pending.account, deviceRegistered: false } });
    this.publish({ recoveryCode });
    await this.afterSignIn();
  }

  /**
   * Restores a backup after logIn. The backup must belong to the signed-in
   * account. A backup made before a password change opens with the recovery
   * code instead, and is then re-wrapped under the current password.
   */
  async restoreAfterLogin(file: BackupFile, recoveryCode?: string): Promise<void> {
    const pending = this.pending;
    if (!pending) throw new DeviceError('Sign in first');
    if (file.account.id !== pending.account.id) throw new DeviceError('This backup belongs to a different account');
    let vaultKey: VaultKey;
    let contents: unknown;
    let keyring = file.keyring;
    if (recoveryCode) {
      ({ vaultKey, contents } = await restoreBackup(file, { recoveryCode }));
      ({ keyring } = await setVaultPassword(file.keyring, vaultKey, pending.password));
    } else {
      ({ vaultKey, contents } = await restoreBackup(file, { password: pending.password }));
    }
    this.pending = null;
    await this.adoptBackup(vaultKey, keyring, contents, pending.loginKey);
    this.publish();
    await this.afterSignIn();
  }

  /**
   * Restores a backup on a device with no vault, with the password or, if it
   * is forgotten, with the recovery code and a new password.
   */
  async restoreBackup(file: BackupFile, secret: { password: string } | { recoveryCode: string; newPassword: string }): Promise<void> {
    if ('password' in secret) {
      const { vaultKey, contents, loginKey } = await restoreBackup(file, { password: secret.password });
      await this.adoptBackup(vaultKey, file.keyring, contents, loginKey ?? null);
      this.publish();
      await this.goOnline();
      return;
    }
    const { vaultKey, contents } = await restoreBackup(file, { recoveryCode: secret.recoveryCode });
    await this.adoptBackup(vaultKey, file.keyring, contents, null);
    try {
      await this.resetPassword(secret.newPassword);
    } catch (err) {
      await this.forgetDevice();
      throw err;
    }
    this.publish();
  }

  async unlock(password: string): Promise<void> {
    const store = await this.db();
    const keyring = await store.readKeyring();
    if (!keyring) throw new DeviceError('There is nothing to unlock on this device');
    const { vaultKey, loginKey } = await unlockWithPassword(keyring, password);
    await this.openSession(vaultKey, keyring, loginKey);
    this.publish();
    await this.goOnline();
  }

  /** Opens the vault with the recovery code and sets a new password, here and on the server. */
  async recover(recoveryCode: string, newPassword: string): Promise<void> {
    const store = await this.db();
    const keyring = await store.readKeyring();
    if (!keyring) throw new DeviceError('There is nothing to recover on this device');
    const vaultKey = await unlockWithRecoveryCode(keyring, recoveryCode);
    await this.openSession(vaultKey, keyring, null);
    try {
      await this.resetPassword(newPassword);
    } catch (err) {
      this.lock();
      throw err;
    }
    this.publish();
  }

  /** Changes the password while unlocked. Other signed-in devices of this account must sign in again. */
  async changePassword(newPassword: string): Promise<void> {
    await this.resetPassword(newPassword);
    this.publish({ notice: null });
  }

  /** Forgets every secret held in memory. A device with a vault shows the unlock screen next. */
  lock(): void {
    const hasVault = this.session !== null || this.state.phase === 'locked';
    this.alerts.stop();
    if (this.answerPoll) clearInterval(this.answerPoll);
    this.answerPoll = null;
    for (const waiting of this.answers.values()) {
      clearTimeout(waiting.timer);
      waiting.reject(new DeviceError('Curentia was locked'));
    }
    this.answers.clear();
    this.practices = new Map();
    this.heldStores.clear();
    this.holders.clear();
    this.seenStore = null;
    this.sharedStore = null;
    this.personalStore = null;
    this.noticesStore = null;
    this.pinCache = null;
    this.api.setToken(null);
    this.api.onReauth(null);
    this.session?.vaultKey.destroy();
    this.session = null;
    this.pending = null;
    this.update({ phase: hasVault ? 'locked' : 'fresh', ...EMPTY });
  }

  /** Deletes this account's data from this device. Without a backup, it cannot be brought back. */
  async forgetDevice(): Promise<void> {
    this.lock();
    await (await this.db()).wipe();
    this.update({ phase: 'fresh', ...EMPTY });
  }

  // ---- Backup and recovery code ----------------------------------------------

  async exportBackup(): Promise<{ filename: string; text: string }> {
    const s = this.requireSession();
    const store = await this.db();
    const when = this.now().toISOString();
    const settings = { ...s.settings, lastBackupAt: when };
    const items: Record<string, unknown> = {};
    for (const key of await store.keys()) items[key] = await store.get(s.vaultKey, key);
    items[ITEM.settings] = settings;
    const file = await createBackup(s.vaultKey, s.keyring, { id: s.profile.account.id, type: s.profile.account.type }, { v: 1, items }, when);
    await this.saveSettings(settings);
    this.publish();
    return { filename: `curentia-backup-${when.slice(0, 10)}.json`, text: `${JSON.stringify(file, null, 2)}\n` };
  }

  async setBackupReminders(enabled: boolean, everyDays: number): Promise<void> {
    const s = this.requireSession();
    await this.saveSettings({ ...s.settings, backupReminders: enabled, backupEveryDays: everyDays });
    this.publish();
  }

  /** Replaces the recovery code. Backups made earlier still open with the code they were made with. */
  async newRecoveryCode(): Promise<void> {
    const s = this.requireSession();
    const { keyring, recoveryCode } = await replaceRecoveryCode(s.keyring, s.vaultKey);
    await (await this.db()).writeKeyring(keyring);
    s.keyring = keyring;
    await this.saveSettings({ ...s.settings, recoveryCodeConfirmed: false });
    this.publish({ recoveryCode });
  }

  async confirmRecoveryCode(): Promise<void> {
    const s = this.requireSession();
    await this.saveSettings({ ...s.settings, recoveryCodeConfirmed: true });
    this.publish({ recoveryCode: null });
  }

  /** Makes this device the record holder of a solo practice or a pharmacy the account runs. */
  async holdPracticeRecords(practiceId: string): Promise<PublicPractice> {
    const s = this.requireSession();
    const { practice } = await this.api.setRecordHolder(practiceId, s.keys.id);
    await this.loadPractices();
    this.update({ practicesVersion: this.state.practicesVersion + 1 });
    return practice;
  }

  // ---- Practice records ------------------------------------------------------------

  /**
   * The account's practices, fresh from the server. Cached records of
   * practices the account has left are deleted from this device.
   */
  async loadPractices(): Promise<MyPractice[]> {
    const s = this.requireSession();
    if (!worksInPractices(s.profile.account.type)) return [];
    const { practices } = await this.api.practices();
    this.practices = new Map(practices.map((p) => [p.practice.id, p]));
    const store = await this.db();
    for (const key of await store.keys()) {
      const left = /^cache:([a-f0-9]{24}):/.exec(key)?.[1];
      if (left && !this.practices.has(left)) await store.remove(key);
    }
    const pins = await this.pins();
    const kept = Object.fromEntries(Object.entries(pins).filter(([id]) => this.practices.has(id)));
    if (Object.keys(kept).length !== Object.keys(pins).length) await this.savePins(kept);
    return practices;
  }

  private async practiceById(practiceId: string): Promise<MyPractice | undefined> {
    try {
      const mine = await this.api.practice(practiceId);
      this.practices.set(practiceId, mine);
      return mine;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        this.practices.delete(practiceId);
        return undefined;
      }
      throw err;
    }
  }

  /** One practice's records, through its hub or, on the record holder, from this device. */
  async records(practiceId: string): Promise<PracticeRecords> {
    const s = this.requireSession();
    const mine = await this.practiceById(practiceId);
    if (!mine) throw new DeviceError('You are not a member of this practice.');
    const local = mine.practice.recordHolderDeviceIds.includes(s.keys.id);
    const channel = { ask: (query: PracticeQuery) => this.ask(mine.practice, mine.roles, query) };
    return new PracticeRecords(mine.practice, mine.roles, channel, local ? null : new RecordCache(await this.db(), s.vaultKey, practiceId));
  }

  /**
   * When the server names a different record holder than the one this device
   * first used for the practice, both device IDs, so the person can check the
   * new hub's fingerprint before trusting it. Null when nothing changed.
   */
  async holderChange(practiceId: string): Promise<{ pinned: string; current: string } | null> {
    const mine = this.practices.get(practiceId) ?? (await this.practiceById(practiceId));
    const pinned = (await this.pins())[practiceId];
    const current = mine?.practice.recordHolderDeviceIds[0];
    return pinned && current && pinned !== current ? { pinned, current } : null;
  }

  /** Trusts the practice's current record holder after the person checked its fingerprint. */
  async trustHolder(practiceId: string): Promise<void> {
    const mine = await this.practiceById(practiceId);
    const current = mine?.practice.recordHolderDeviceIds[0];
    if (!current) throw new PracticeError('no_holder', 'This practice has no record holder yet.');
    await this.savePins({ ...(await this.pins()), [practiceId]: current });
  }

  private async pins(): Promise<Record<string, string>> {
    const s = this.requireSession();
    this.pinCache ??= (await (await this.db()).get<Record<string, string>>(s.vaultKey, 'pins')) ?? {};
    return this.pinCache;
  }

  private async savePins(pins: Record<string, string>): Promise<void> {
    const s = this.requireSession();
    await (await this.db()).put(s.vaultKey, 'pins', pins);
    this.pinCache = pins;
  }

  private seen(): VaultSeen {
    const s = this.requireSession();
    if (!this.store) throw new DeviceError('Storage is not open');
    this.seenStore ??= new VaultSeen(this.store, s.vaultKey);
    return this.seenStore;
  }

  private shared(): VaultShared {
    const s = this.requireSession();
    if (!this.store) throw new DeviceError('Storage is not open');
    this.sharedStore ??= new VaultShared(this.store, s.vaultKey);
    return this.sharedStore;
  }

  private openedStore(): LocalStore {
    if (!this.store) throw new DeviceError('Storage is not open');
    return this.store;
  }

  private heldStore(practiceId: string): VaultRecordStore {
    const s = this.requireSession();
    if (!this.store) throw new DeviceError('Storage is not open');
    let held = this.heldStores.get(practiceId);
    if (!held) {
      held = VaultRecordStore.forPractice(this.store, s.vaultKey, practiceId);
      this.heldStores.set(practiceId, held);
    }
    return held;
  }

  /** The record holder this device trusts for the practice. The first one used is remembered. */
  private async checkedHolder(practice: PublicPractice): Promise<string> {
    const current = practice.recordHolderDeviceIds[0];
    if (!current) throw new PracticeError('no_holder', 'This practice has no hub paired yet. Ask its practice admin to pair one.');
    const pins = await this.pins();
    const pinned = pins[practice.id];
    if (!pinned) {
      await this.savePins({ ...pins, [practice.id]: current });
      return current;
    }
    if (pinned !== current) {
      throw new PracticeError('holder_changed', 'This practice’s hub has changed. Check its fingerprint with your practice admin before using it.');
    }
    return current;
  }

  private async ask(practice: PublicPractice, roles: Role[], query: PracticeQuery): Promise<PracticeResponse> {
    const s = this.requireSession();
    const request = { v: 1, requestId: globalThis.crypto.randomUUID(), ...query } as PracticeRequest;
    if (practice.recordHolderDeviceIds.includes(s.keys.id)) {
      const response = await answerPracticeRequest(this.heldStore(practice.id), { accountId: s.profile.account.id, roles }, request);
      // A dispensing record written here goes to the patient and the prescribing doctor as a notice.
      if (response.ok && response.result.op === 'put' && response.result.record.kind === 'dispense') {
        void this.holderFor(practice.id).notifyDispensed().catch(() => undefined);
      }
      return response;
    }
    const holderId = await this.checkedHolder(practice);
    const { devices } = await this.api.practiceKeys(practice.id);
    const holder = devices.find((d) => d.id === holderId);
    if (!holder) throw new PracticeError('no_holder', 'The practice’s hub is not available. Ask its practice admin to check it.');
    const sealed = await sealEnvelope({
      kind: 'practice_request',
      sender: s.keys,
      recipient: peerFromDirectory(holder),
      plaintext: jsonBytes(request),
      toPractice: practice.id,
    });
    const answer = this.waitForAnswer(request.requestId);
    try {
      await this.api.sendEnvelopes([sealed.wire]);
    } catch (err) {
      this.settle(request.requestId);
      throw err;
    }
    return answer;
  }

  private waitForAnswer(requestId: string): Promise<PracticeResponse> {
    const answer = new Promise<PracticeResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.settle(requestId);
        reject(new PracticeError('timeout', 'The practice’s hub did not answer. It may be switched off or offline.'));
      }, ANSWER_TIMEOUT_MS);
      this.answers.set(requestId, { resolve, reject, timer });
    });
    // Live alerts usually bring the answer; looking now and then covers a missed one.
    this.answerPoll ??= setInterval(() => void this.checkInbox(), this.options.answerPollMs ?? ANSWER_POLL_MS);
    return answer;
  }

  private settle(requestId: string): Waiting | undefined {
    const waiting = this.answers.get(requestId);
    if (waiting) clearTimeout(waiting.timer);
    this.answers.delete(requestId);
    if (this.answers.size === 0 && this.answerPoll) {
      clearInterval(this.answerPoll);
      this.answerPoll = null;
    }
    return waiting;
  }

  /**
   * Opens what waits for this device: answers from the record holders of the
   * account's practices; requests, history shares and prescriptions for
   * practices whose records this device holds; and on a patient's phone, the
   * prescriptions and records their practices send. Other envelopes stay for
   * later phases and are counted as waiting. Runs one at a time.
   */
  checkInbox(): Promise<void> {
    if (this.inboxRun) {
      this.inboxAgain = true;
      return this.inboxRun;
    }
    this.inboxRun = (async () => {
      try {
        do {
          this.inboxAgain = false;
          await this.inboxOnce();
        } while (this.inboxAgain);
      } finally {
        this.inboxRun = null;
      }
    })();
    return this.inboxRun;
  }

  private async inboxOnce(): Promise<void> {
    const s = this.session;
    if (!s) return;
    let page: { envelopes: InboxEnvelope[]; more: boolean };
    try {
      page = await this.api.inbox(s.keys.id);
    } catch (err) {
      if (err instanceof ApiError) return;
      throw err;
    }
    const held = new Set([...this.practices.values()].filter((p) => p.practice.recordHolderDeviceIds.includes(s.keys.id)).map((p) => p.practice.id));
    const patient = s.profile.account.type === 'patient';
    const toAnswer = new Set<string>();
    let handled = 0;
    let waiting = 0;
    // The patient's links are fetched once per page, and only when something from a practice arrived.
    let links: Promise<MyPatientLink[]> | null = null;
    const myLinks = () => (links ??= this.api.myLinks().then((r) => r.links));
    for (const envelope of page.envelopes) {
      if (envelope.kind === 'practice_response') {
        await this.takeAnswer(envelope);
        handled += 1;
        continue;
      }
      const practiceId = (HOLDER_KINDS as readonly string[]).includes(envelope.kind) ? addressedPractice(envelope) : undefined;
      if (practiceId && held.has(practiceId)) {
        toAnswer.add(practiceId);
      } else if (patient && !practiceId && (envelope.kind === 'prescription' || envelope.kind === 'care_record')) {
        await this.takeDelivery(envelope, myLinks);
        handled += 1;
      } else if ((patient || s.profile.account.type === 'doctor') && envelope.kind === 'dispense_notice') {
        await this.takeNotice(envelope);
        handled += 1;
      } else {
        waiting += 1;
      }
    }
    for (const practiceId of toAnswer) {
      const holder = this.holderFor(practiceId);
      await holder.drain();
      // Notices that could not go earlier are tried again whenever the holder is busy anyway.
      void holder.notifyDispensed().catch(() => undefined);
      handled += 1;
    }
    if (this.session) this.update({ waiting: waiting + (page.more && handled === 0 ? 1 : 0) });
    if (page.more && handled > 0) this.inboxAgain = true;
  }

  /** Takes in a record holder's answer after checking where it came from, then resolves the request waiting for it. */
  private async takeAnswer(envelope: InboxEnvelope): Promise<void> {
    const s = this.requireSession();
    const drop = () => this.api.ackEnvelopes(s.keys.id, [envelope.id]).then(() => undefined, () => undefined);
    if (await this.seen().has(envelope.id)) return drop();
    let header;
    try {
      header = decodeEnvelopeHeader(fromBase64Url(envelope.header));
    } catch {
      return drop();
    }
    const practiceId = header.from.practice;
    const mine = practiceId ? (this.practices.get(practiceId) ?? (await this.practiceById(practiceId))) : undefined;
    const pinned = practiceId ? (await this.pins())[practiceId] : undefined;
    if (!mine || !mine.practice.recordHolderDeviceIds.includes(header.from.device) || (pinned && pinned !== header.from.device)) {
      return drop();
    }
    let response: PracticeResponse;
    try {
      const { device } = await this.api.deviceKeys(header.from.device);
      const opened = await openEnvelope({ recipient: s.keys, envelope, sender: peerFromDirectory(device) });
      response = practiceResponseSchema.parse(parseJsonBytes(opened.plaintext));
    } catch (err) {
      if (err instanceof ApiError && err.offline) return;
      return drop();
    }
    await this.seen().add(envelope.id, header.createdAt);
    await drop();
    const waiting = this.settle(response.requestId);
    if (waiting) waiting.resolve(response);
    else if (response.ok && response.result.op === 'put') {
      // A late answer to a write still updates what this device shows.
      await new RecordCache(await this.db(), s.vaultKey, mine.practice.id).merge(response.result.record);
    }
  }

  /**
   * The record holder side when this device holds the practice's records: it
   * answers members with their roles checked on every request, takes in
   * history shared with the practice, and shares the practice's own records
   * when a patient names it as a source.
   */
  private holderFor(practiceId: string): HolderService {
    const s = this.requireSession();
    let holder = this.holders.get(practiceId);
    if (!holder) {
      const deviceId = s.keys.id;
      holder = new HolderService({
        practiceId,
        keys: s.keys,
        store: this.heldStore(practiceId),
        seen: this.seen(),
        shared: this.shared(),
        transport: {
          inbox: async () => (await this.api.inbox(deviceId, 50, HOLDER_KINDS)).envelopes.filter((e) => addressedPractice(e) === practiceId),
          ack: async (ids) => void (await this.api.ackEnvelopes(deviceId, ids)),
          send: (envelopes) => this.sendAll(envelopes),
          members: async () => (await this.api.holderMembers(practiceId)).members,
          consent: async (id) => (await notFoundAsNull(this.api.consent(id)))?.consent ?? null,
          sourceConsents: async () => (await this.allConsents({ practiceId, status: 'approved' })).filter((c) => c.approval?.sources.includes(practiceId)),
          device: async (id) => {
            const found = await notFoundAsNull(this.api.deviceKeys(id));
            return found ? { ...found.device } : null;
          },
          holders: async (id) => (await notFoundAsNull(this.api.practiceKeys(id)))?.devices ?? [],
          accountDevices: async (id) => (await notFoundAsNull(this.api.accountKeys(id)))?.devices ?? [],
        },
        notices: new VaultNoticeCursor(this.openedStore(), s.vaultKey, practiceId),
        onEvent: (event: HolderEvent) => {
          if ((event.type === 'received' || event.type === 'prescribed') && this.session) this.update({ recordsVersion: this.state.recordsVersion + 1 });
        },
      });
      this.holders.set(practiceId, holder);
    }
    return holder;
  }

  /** Sends envelopes in batches the relay accepts. */
  private async sendAll(envelopes: { header: string; ciphertext: string; signature: string }[]): Promise<void> {
    for (let i = 0; i < envelopes.length; i += ENVELOPE_MAX_BATCH) await this.api.sendEnvelopes(envelopes.slice(i, i + ENVELOPE_MAX_BATCH));
  }

  /** Every page of a consent listing. */
  private async allConsents(query: Parameters<Api['consents']>[0]): Promise<PublicConsent[]> {
    const found: PublicConsent[] = [];
    let before: string | undefined;
    do {
      const page = await this.api.consents({ ...query, ...(before ? { before } : {}) });
      found.push(...page.consents);
      before = page.next ?? undefined;
    } while (before);
    return found;
  }

  /** Clinical practices whose records this device holds: the ones a patient can name as a source. */
  private heldClinicalPractices(): string[] {
    const s = this.session;
    if (!s) return [];
    return [...this.practices.values()]
      .filter((p) => p.practice.type !== 'pharmacy' && p.practice.recordHolderDeviceIds.includes(s.keys.id))
      .map((p) => p.practice.id);
  }

  /** As a record holder: shares under approvals that name a practice this device holds as a source, once each. */
  async shareHeld(): Promise<void> {
    for (const practiceId of this.heldClinicalPractices()) await this.holderFor(practiceId).shareApproved();
  }

  /** As a record holder: sends the dispensing notices still owed by every practice this device holds. */
  async notifyHeld(): Promise<void> {
    const s = this.session;
    if (!s) return;
    for (const mine of this.practices.values()) {
      if (mine.practice.recordHolderDeviceIds.includes(s.keys.id)) await this.holderFor(mine.practice.id).notifyDispensed();
    }
  }

  // ---- The patient's own records and their consents ------------------------------

  /** The patient's own records on this phone. */
  personal(): PersonalRecords {
    const s = this.requireSession();
    if (s.profile.account.type !== 'patient') throw new DeviceError('Only patients keep their own records here');
    if (!this.store) throw new DeviceError('Storage is not open');
    this.personalStore ??= new PersonalRecords(this.store, s.vaultKey, s.profile.account);
    return this.personalStore;
  }

  /**
   * Approves a request for history: this device signs every term, with the
   * practices the patient allows to send their records, then sends this
   * phone's own records that the consent covers. Returns how many it sent.
   */
  async approveConsent(consent: PublicConsent, sources: string[]): Promise<{ consent: PublicConsent; sent: number }> {
    const s = this.requireSession();
    const approval = { deviceId: s.keys.id, sources, signedAt: this.now().toISOString() };
    const signature = await signBytes(s.keys.signingPrivate, consentApprovalBytes(consentTermsOf(consent), approval));
    const { consent: approved } = await this.api.approveConsent(consent.id, { ...approval, signature: toBase64Url(signature) });
    this.update({ consentsVersion: this.state.consentsVersion + 1 });
    const sent = await this.shareUnder(approved);
    return { consent: approved, sent };
  }

  async denyConsent(consentId: string): Promise<PublicConsent> {
    const { consent } = await this.api.denyConsent(consentId);
    this.update({ consentsVersion: this.state.consentsVersion + 1 });
    return consent;
  }

  async revokeConsent(consentId: string): Promise<PublicConsent> {
    const { consent } = await this.api.revokeConsent(consentId);
    this.update({ consentsVersion: this.state.consentsVersion + 1 });
    return consent;
  }

  /**
   * Sends this phone's records under an approved consent to the record holder
   * of the practice that asked, once. A practice with no record holder yet is
   * tried again on the next sign-in. Returns how many records went.
   */
  private async shareUnder(consent: PublicConsent): Promise<number> {
    const s = this.requireSession();
    if (consent.status !== 'approved' || (await this.shared().has(consent.id))) return 0;
    const terms = consentTermsOf(consent);
    const documents = await this.personal().under(terms);
    if (documents.length > 0) {
      const { devices } = await this.api.practiceKeys(consent.requesterPracticeId);
      if (devices.length === 0) return 0;
      await this.sendAll(await sealHistoryShares({ sender: s.keys, terms, documents, recipients: devices.map(peerFromDirectory) }));
    }
    await this.shared().add(consent.id, consent.expiresAt);
    return documents.length;
  }

  /** Sends what earlier approvals still owe, for example after the phone was offline. Returns how many records went. */
  async shareMine(): Promise<number> {
    if (this.session?.profile.account.type !== 'patient') return 0;
    let sent = 0;
    for (const consent of await this.allConsents({ status: 'approved' })) sent += await this.shareUnder(consent);
    return sent;
  }

  /**
   * Keeps a prescription or record a practice sent to this patient's phone,
   * after checking it: the sender is a doctor's active device, or a record
   * holder its practice lists; the practice is one the patient is linked with
   * now; and a prescription carries the sending doctor's valid signature. The
   * name shown for the practice is the one from the patient's own links.
   */
  private async takeDelivery(envelope: InboxEnvelope, links: () => Promise<MyPatientLink[]>): Promise<void> {
    const s = this.requireSession();
    const drop = () => this.api.ackEnvelopes(s.keys.id, [envelope.id]).then(() => undefined, () => undefined);
    if (await this.seen().has(envelope.id)) return drop();
    let header;
    try {
      header = decodeEnvelopeHeader(fromBase64Url(envelope.header));
    } catch {
      return drop();
    }
    if (header.to.device !== s.keys.id || header.to.practice !== undefined) return drop();
    if (Date.parse(header.createdAt) < this.now().getTime() - RELAY_TTL_DAYS * DAY_MS) return drop();
    const fromPractice = header.from.practice;
    let sender: Awaited<ReturnType<Api['deviceKeys']>>['device'];
    let payload: DeliveryPayload;
    let link: MyPatientLink | undefined;
    try {
      ({ device: sender } = await this.api.deviceKeys(header.from.device));
      if (sender.revokedAt) return drop();
      if (fromPractice === undefined ? sender.accountType !== 'doctor' : header.kind !== 'care_record') return drop();
      const senderId = sender.id;
      if (fromPractice !== undefined && !(await this.api.practiceKeys(fromPractice)).devices.some((d) => d.id === senderId)) return drop();
      const opened = await openEnvelope({ recipient: s.keys, envelope, sender: peerFromDirectory(sender) });
      const parsed = deliveryPayloadSchema.safeParse(parseJsonBytes(opened.plaintext));
      if (!parsed.success) return drop();
      payload = parsed.data;
      link = (await links()).find((l) => l.status === 'active' && l.practice.id === payload.practice.id);
    } catch (err) {
      // Offline: leave it in the relay for the next look.
      if (err instanceof ApiError && err.offline) return;
      return drop();
    }
    if (!link || (fromPractice !== undefined && fromPractice !== payload.practice.id)) return drop();
    // Prescriptions travel only as prescriptions, signed on the doctor's own device.
    const prescribed = payload.record.hiType === 'Prescription';
    if (prescribed !== (header.kind === 'prescription')) return drop();
    if (prescribed) {
      const bundle = payload.record.resource as unknown as fhir.Bundle;
      const signer = fhir.documentSigner(bundle);
      if (signer?.deviceId !== sender.id || !(await fhir.verifyDocumentSignature(bundle, fromBase64Url(sender.signingKey)))) return drop();
    }
    try {
      await this.personal().take({ payload, practiceName: link.practice.name, senderId: sender.accountId, now: this.now() });
    } catch (err) {
      if (err instanceof ShareRefusal) return drop();
      throw err;
    }
    await this.seen().add(envelope.id, header.createdAt);
    await drop();
    if (this.session) this.update({ recordsVersion: this.state.recordsVersion + 1 });
  }

  /**
   * Keeps a dispensing notice, on a patient's phone or a prescribing doctor's
   * device. The sender is the record holder of the practice that dispensed,
   * which the relay checked. Here the device checks that the prescription in
   * the notice carries a doctor's valid signature, and that it is this
   * patient's prescription or this doctor's own, before keeping anything. So a
   * pharmacy can only report on prescriptions it really holds.
   */
  private async takeNotice(envelope: InboxEnvelope): Promise<void> {
    const s = this.requireSession();
    const me = s.profile.account;
    const drop = () => this.api.ackEnvelopes(s.keys.id, [envelope.id]).then(() => undefined, () => undefined);
    if (await this.seen().has(envelope.id)) return drop();
    let header;
    try {
      header = decodeEnvelopeHeader(fromBase64Url(envelope.header));
    } catch {
      return drop();
    }
    const dispensedBy = header.from.practice;
    if (header.to.device !== s.keys.id || header.to.practice !== undefined || dispensedBy === undefined) return drop();
    if (Date.parse(header.createdAt) < this.now().getTime() - RELAY_TTL_DAYS * DAY_MS) return drop();
    let payload;
    let senderId: string;
    let pharmacyName: string;
    try {
      const { device: sender } = await this.api.deviceKeys(header.from.device);
      senderId = sender.accountId;
      const opened = await openEnvelope({ recipient: s.keys, envelope, sender: peerFromDirectory(sender) });
      const parsed = dispenseNoticePayloadSchema.safeParse(parseJsonBytes(opened.plaintext));
      if (!parsed.success || parsed.data.dispensedBy !== dispensedBy) return drop();
      payload = parsed.data;
      const prescription = payload.prescription.resource as unknown as fhir.Bundle;
      const signer = fhir.documentSigner(prescription);
      if (!signer) return drop();
      // Signatures made before a device was revoked still check out against its key.
      const { device: signerDevice } = await this.api.deviceKeys(signer.deviceId);
      if (signerDevice.accountType !== 'doctor' || !(await fhir.verifyDocumentSignature(prescription, fromBase64Url(signerDevice.signingKey)))) return drop();
      if (me.type === 'doctor' && signerDevice.accountId !== me.id) return drop();
      pharmacyName = (await notFoundAsNull(this.api.directoryPractice(dispensedBy)))?.practice.name ?? 'A pharmacy';
    } catch (err) {
      if (err instanceof ApiError && err.offline) return;
      return drop();
    }
    try {
      if (me.type === 'patient') {
        await this.personal().takeDispense({ payload, pharmacyName, senderId, now: this.now() });
      } else {
        const prescription = fhir.readPrescription(payload.prescription.resource as unknown as fhir.Bundle);
        const dispense = fhir.readDispenseRecord(payload.dispense.resource as unknown as fhir.Bundle);
        if (!fhir.dispenseMatchesPrescription(dispense, prescription)) return drop();
        await this.doctorNotices().take({
          key: `${dispensedBy}:${payload.dispense.id}`,
          pharmacy: { id: dispensedBy, name: pharmacyName },
          prescriptionId: prescription.id,
          patientName: prescription.patient.name,
          prescribedAt: prescription.date,
          dispense: payload.dispense,
          receivedAt: this.now().toISOString(),
        });
      }
    } catch (err) {
      if (err instanceof ShareRefusal || err instanceof fhir.DocumentError) return drop();
      throw err;
    }
    await this.seen().add(envelope.id, header.createdAt);
    await drop();
    if (this.session) this.update({ recordsVersion: this.state.recordsVersion + 1 });
  }

  /** On a doctor's device: the dispensing notices pharmacies sent about the doctor's prescriptions. */
  doctorNotices(): DoctorNotices {
    const s = this.requireSession();
    if (s.profile.account.type !== 'doctor') throw new DeviceError('Only doctors receive dispensing notices');
    this.noticesStore ??= new DoctorNotices(this.openedStore(), s.vaultKey);
    return this.noticesStore;
  }

  /**
   * On a patient's phone: pharmacies the patient used, to send a prescription
   * to again. The ones they sent prescriptions to come first, newest first,
   * then the ones that dispensed for them. A city is known only for the first.
   */
  async recentPharmacies(): Promise<{ id: string; name: string; city?: string }[]> {
    const s = this.requireSession();
    const sent = (await this.openedStore().get<DirectoryPharmacy[]>(s.vaultKey, 'recent-pharmacies')) ?? [];
    const dispensed = (await this.personal().dispensers()).filter((d) => !sent.some((p) => p.id === d.id));
    return [...sent, ...dispensed].slice(0, 5);
  }

  private async rememberPharmacy(pharmacy: { id: string; name: string; city?: string }): Promise<void> {
    const s = this.requireSession();
    const sent = (await this.openedStore().get<DirectoryPharmacy[]>(s.vaultKey, 'recent-pharmacies')) ?? [];
    const recent = [{ id: pharmacy.id, name: pharmacy.name, city: pharmacy.city ?? '' }, ...sent.filter((p) => p.id !== pharmacy.id)].slice(0, 5);
    await this.openedStore().put(s.vaultKey, 'recent-pharmacies', recent);
  }

  // ---- Prescriptions and copies for patients -----------------------------------------

  /** Signs a document as its author with this device's key. The device ID is the fingerprint printed with it. */
  async signDocument(bundle: fhir.Bundle): Promise<fhir.Bundle> {
    const s = this.requireSession();
    return fhir.signDocument(bundle, { deviceId: s.keys.id, signingKey: s.keys.signingPrivate }, this.now());
  }

  /**
   * Sends a copy of a practice's document to the patient's phone: a signed
   * prescription, or a care record such as readings taken at the clinic. The
   * relay lets it through only while the patient is linked with one of this
   * doctor's practices. Returns how many of the patient's devices it went to.
   */
  async sendToPatient(record: HeldRecord, practice: { id: string; name: string }, patientAccountId: string): Promise<number> {
    const s = this.requireSession();
    const { devices } = await this.api.accountKeys(patientAccountId);
    if (devices.length === 0) return 0;
    const kind = record.hiType === 'Prescription' ? 'prescription' : 'care_record';
    await this.sendAll(await sealDeliveries({ kind, sender: s.keys, payload: deliveryOf(record, practice), recipients: devices.map(peerFromDirectory) }));
    return devices.length;
  }

  /** Sends a prescription to a pharmacy's record holder: the doctor's linked pharmacy, or the one a patient picked. */
  async sendToPharmacy(record: Pick<HeldRecord, 'id' | 'version' | 'hiType' | 'resource'>, practice: { id: string; name: string }, pharmacyId: string): Promise<void> {
    const s = this.requireSession();
    if (record.hiType !== 'Prescription') throw new DeviceError('Only prescriptions go to a pharmacy.');
    const { devices } = await this.api.practiceKeys(pharmacyId);
    if (devices.length === 0) throw new DeviceError('That pharmacy has not set up the device that receives prescriptions yet.');
    const payload = deliveryOf(record, practice);
    await this.sendAll(await sealDeliveries({ kind: 'prescription', sender: s.keys, payload, recipients: devices.map(peerFromDirectory), toPractice: pharmacyId }));
  }

  /** On a patient's phone: passes a prescription a practice sent on to the pharmacy the patient chose, and remembers the pharmacy. */
  async forwardPrescription(record: HeldRecord, pharmacy: { id: string; name: string; city?: string }): Promise<void> {
    const source = record.source;
    if (record.hiType !== 'Prescription' || source?.from !== 'practice' || !source.practiceId) {
      throw new DeviceError('Only a prescription from your doctor can be sent to a pharmacy.');
    }
    await this.sendToPharmacy(
      { id: source.recordId, version: source.version, hiType: record.hiType, resource: record.resource },
      { id: source.practiceId, name: source.practiceName ?? 'Your doctor’s practice' },
      pharmacy.id,
    );
    await this.rememberPharmacy(pharmacy);
  }

  /** A doctor's or pharmacist's qualifications, printed on what they sign. */
  async setQualifications(qualifications: string): Promise<void> {
    const s = this.requireSession();
    const { account } = await this.api.setQualifications(qualifications);
    await this.saveProfile({ ...s.profile, account });
    this.publish();
  }

  // ---- Linking patients ----------------------------------------------------------

  /**
   * Links the patient whose code was scanned or typed to a practice. A scanned
   * code also names the patient's device; the account the server links must
   * have that device, which confirms in person that it is the right account.
   */
  async linkPatient(practiceId: string, scannedOrTyped: string): Promise<PracticePatientLink> {
    const parsed = parseLinkCode(scannedOrTyped);
    if (!parsed) throw new DeviceError('Enter the 10-character code from the patient’s phone.');
    const { link } = await this.api.linkWithCode(practiceId, parsed.code);
    this.update({ linksVersion: this.state.linksVersion + 1 });
    if (parsed.deviceId && !link.deviceIds.includes(parsed.deviceId)) {
      await this.api.removeLink(link.id).catch(() => undefined);
      throw new DeviceError('The code’s phone does not belong to the account the server linked. The link was undone. Ask the patient to show a new code.');
    }
    return link;
  }

  /** Checks with the server again, for example while waiting for verification. */
  async refresh(): Promise<void> {
    this.requireSession();
    await this.goOnline();
    if (this.session) this.publish();
  }

  // ---- Internals -----------------------------------------------------------------

  private async openSession(vaultKey: VaultKey, keyring: VaultKeyring, loginKey: string | null): Promise<void> {
    const store = await this.db();
    const profile = await store.get<Profile>(vaultKey, ITEM.profile);
    const material = await store.get<DeviceKeyMaterial>(vaultKey, ITEM.device);
    if (!profile || !material) {
      vaultKey.destroy();
      throw new DeviceError('The data on this device is incomplete. Restore a backup or remove it from this device.');
    }
    const settings = { ...DEFAULT_SETTINGS, ...(await store.get<Settings>(vaultKey, ITEM.settings)) };
    this.session = { vaultKey, keyring, profile, settings, keys: await loadDeviceKeys(material), material, loginKey };
    this.installReauth();
  }

  /** Writes a new vault, replacing anything stored before. The keyring goes last: it marks the vault complete. */
  private async adopt(next: Omit<Session, 'settings'> & { settings?: Settings; extraItems?: Record<string, unknown> }): Promise<void> {
    const store = await this.db();
    const settings = next.settings ?? DEFAULT_SETTINGS;
    await store.wipe();
    for (const [key, value] of Object.entries(next.extraItems ?? {})) await store.put(next.vaultKey, key, value);
    await store.put(next.vaultKey, ITEM.profile, next.profile);
    await store.put(next.vaultKey, ITEM.device, next.material);
    await store.put(next.vaultKey, ITEM.settings, settings);
    await store.writeKeyring(next.keyring);
    this.session = { ...next, settings };
    this.installReauth();
  }

  private async adoptBackup(vaultKey: VaultKey, keyring: VaultKeyring, contents: unknown, loginKey: string | null): Promise<void> {
    const parsed = backupContentsSchema.safeParse(contents);
    const items = parsed.success ? parsed.data.items : null;
    const profile = items?.[ITEM.profile] as Profile | undefined;
    const material = items?.[ITEM.device] as DeviceKeyMaterial | undefined;
    if (!items || !profile?.account?.id || !material) throw new DeviceError('This backup is damaged or from another app');
    const { [ITEM.profile]: _p, [ITEM.device]: _d, [ITEM.settings]: savedSettings, ...extraItems } = items;
    await this.adopt({
      vaultKey,
      keyring,
      profile,
      material,
      keys: await loadDeviceKeys(material),
      loginKey,
      settings: { ...DEFAULT_SETTINGS, ...(savedSettings as Partial<Settings> | undefined), recoveryCodeConfirmed: true },
      extraItems,
    });
  }

  private installReauth(): void {
    this.api.onReauth(async () => {
      const s = this.session;
      if (!s?.loginKey) return false;
      try {
        const result = await this.api.login(s.profile.account.type, s.profile.account.mobile, s.loginKey);
        this.api.setToken(result.accessToken);
        return true;
      } catch {
        return false;
      }
    });
  }

  private async saveProfile(profile: Profile): Promise<void> {
    const s = this.requireSession();
    await (await this.db()).put(s.vaultKey, ITEM.profile, profile);
    s.profile = profile;
  }

  private async saveSettings(settings: Settings): Promise<void> {
    const s = this.requireSession();
    await (await this.db()).put(s.vaultKey, ITEM.settings, settings);
    s.settings = settings;
  }

  /** Signs in with the login key held in memory. Offline is not an error: local data stays usable. */
  private async goOnline(): Promise<void> {
    const s = this.requireSession();
    if (!s.loginKey) {
      this.update({ online: false });
      return;
    }
    let result: AuthResult;
    try {
      result = await this.api.login(s.profile.account.type, s.profile.account.mobile, s.loginKey);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      if (err.offline) {
        this.update({ online: false });
      } else if (err.code === 'account_rejected' || err.code === 'account_suspended') {
        const status = err.code === 'account_rejected' ? 'rejected' : 'suspended';
        const reason = typeof err.extra.reason === 'string' ? err.extra.reason : undefined;
        const { statusReason: _old, ...account } = s.profile.account;
        await this.saveProfile({ ...s.profile, account: { ...account, status, ...(reason ? { statusReason: reason } : {}) } });
        this.update({ online: false, account: this.session?.profile.account ?? null });
      } else if (err.code === 'invalid_credentials') {
        this.update({ online: false, notice: 'The server did not accept this password. If you changed it on another device, unlock with the new one.' });
      } else {
        this.update({ online: false, notice: err.message });
      }
      return;
    }
    this.api.setToken(result.accessToken);
    await this.saveProfile({ ...s.profile, account: result.account });
    this.update({ account: result.account, notice: null });
    await this.afterSignIn();
  }

  /**
   * After any successful sign-in: make sure this device is registered, then
   * start alerts and count the inbox. A server failure here leaves the device
   * unlocked and offline; the next unlock or refresh tries again.
   */
  private async afterSignIn(): Promise<void> {
    try {
      await this.checkDevice();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      this.update({ online: false, notice: err.offline ? null : err.message });
      return;
    }
    this.update({ online: true, notice: this.state.notice });
    if (this.options.liveAlerts !== false) this.startAlerts();
    try {
      await this.loadPractices();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    await this.checkInbox();
    // Records owed under approvals made while this device was locked or offline, and notices not yet sent.
    if (this.session?.profile.account.type === 'patient') void this.shareMine().catch(() => undefined);
    else void this.shareHeld().then(() => this.notifyHeld()).catch(() => undefined);
  }

  /** Registers this device's keys, or replaces them if the account revoked them. */
  private async checkDevice(): Promise<void> {
    const s = this.requireSession();
    const { devices } = await this.api.devices();
    const mine = devices.find((d) => d.id === s.keys.id);
    if (mine && !mine.revokedAt) {
      if (!s.profile.deviceRegistered) await this.saveProfile({ ...s.profile, deviceRegistered: true });
      return;
    }
    if (mine?.revokedAt) {
      const { keys, material } = await generateDeviceKeys();
      await (await this.db()).put(s.vaultKey, ITEM.device, material);
      s.keys = keys;
      s.material = material;
      this.update({ deviceId: keys.id, notice: 'This device had been removed from your account, so it now has new keys.' });
    }
    await this.api.registerDevice(await deviceRegistration(s.keys, s.profile.account.id, this.options.label));
    await this.saveProfile({ ...s.profile, deviceRegistered: true });
  }

  private startAlerts(): void {
    const s = this.requireSession();
    const deviceId = s.keys.id;
    this.alerts.start(
      () => ({ token: this.api.currentToken, deviceId }),
      {
        onAlert: (alert: Alert) => {
          if (alert.type === 'envelope' && alert.deviceId === deviceId) void this.checkInbox();
          if (alert.type === 'practice' && this.session) {
            this.update({ practicesVersion: this.state.practicesVersion + 1 });
            void this.loadPractices().catch(() => undefined);
          }
          if (alert.type === 'consent' && this.session) {
            this.update({ consentsVersion: this.state.consentsVersion + 1 });
            if (alert.status === 'approved') void this.shareHeld().catch(() => undefined);
          }
          if (alert.type === 'patient_link' && this.session) this.update({ linksVersion: this.state.linksVersion + 1 });
        },
        onStatus: (connected) => {
          if (this.session) this.update({ live: connected });
        },
        onSessionLost: async () => {
          if (!this.session) return false;
          try {
            this.alerts.stop();
            await this.goOnline();
            return false;
          } catch {
            return false;
          }
        },
      },
    );
  }

  /**
   * Sets a new password: re-wraps the vault key and replaces the server's login
   * key with a request signed by this device. The new keyring is stored only
   * after the server accepts, so a failure leaves the old password working.
   */
  private async resetPassword(newPassword: string): Promise<void> {
    const s = this.requireSession();
    const { keyring, loginKey } = await setVaultPassword(s.keyring, s.vaultKey, newPassword);
    const signedAt = this.now().toISOString();
    const signature = await signBytes(s.keys.signingPrivate, loginResetBytes(s.profile.account.id, s.keys.id, loginKey, signedAt));
    const result = await this.api.resetLoginKey({
      type: s.profile.account.type,
      mobile: s.profile.account.mobile,
      deviceId: s.keys.id,
      newLoginKey: loginKey,
      signedAt,
      signature: toBase64Url(signature),
    });
    await (await this.db()).writeKeyring(keyring);
    s.keyring = keyring;
    s.loginKey = loginKey;
    this.api.setToken(result.accessToken);
    await this.saveProfile({ ...s.profile, account: result.account });
    await this.afterSignIn();
  }
}

export { WrongSecretError };
